import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { CHAIRMAN, US_TEAM, CHINA_TEAM, CHINA_TEAM_RESPONSIBILITIES, ADVISORY_BOARD } from "../data/team";

export default function About() {
  const [selected, setSelected] = useState(null);

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-32 pb-24 bg-[#0B1F3A] text-[#F8F7F2] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1764160233831-694591688066?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lsaW5lfGVufDB8fHx8MTc4MzI3NTM1Nnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/40 to-[#0B1F3A]/95" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="eyebrow" style={{ color: "#D4AF37" }}>About the Firm</div>
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02] max-w-4xl">
            A globally integrated private investment platform.
          </h1>
          <p className="mt-8 max-w-3xl text-[#F8F7F2]/80 text-base md:text-lg leading-relaxed">
            First Providence Capital is a global private investment and strategic advisory firm
            focused on energy, infrastructure, commodities, technology, real assets, and
            transformative investment opportunities across North America, Asia, Africa, and select
            international markets.
          </p>
        </div>
      </section>

      {/* FIRM OVERVIEW */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="eyebrow">Firm Overview</div>
          <div className="hairline mt-6" />
        </div>
        <div className="md:col-span-8 text-body">
          <p className="mb-6">
            The firm brings together accomplished executives from private equity, institutional
            finance, global energy, commercial real estate, insurance, government, law, artificial
            intelligence, and international trade to originate, structure, finance, and execute
            complex transactions — with disciplined risk management and long-term value creation
            at their core.
          </p>
          <p>
            Our professionals have held senior leadership positions within publicly traded
            companies, Fortune 500 organizations, global energy enterprises, government institutions,
            and investment firms. Collectively, they represent
            decades of experience deploying capital, managing operational risk, leading multinational
            organizations, and building enduring strategic partnerships across global markets.
          </p>
        </div>
      </section>

      {/* CHAIRMAN — featured */}
      <section className="bg-white border-y border-[#0B1F3A]/10 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="eyebrow" style={{ color: "#D4AF37" }}>Board Leadership</div>
            <div className="hairline mt-6" />
          </div>
          <div className="md:col-span-8">
            <PersonMonogram name={CHAIRMAN.name} size="xl" />
            <h2 className="font-display text-4xl md:text-5xl mt-6 text-[#0B1F3A]">{CHAIRMAN.name}</h2>
            <div className="mt-2 text-sm uppercase tracking-widest text-[#3399FF]">{CHAIRMAN.role}</div>
            <p className="mt-8 text-body max-w-3xl">{CHAIRMAN.short}</p>
            <button
              onClick={() => setSelected(CHAIRMAN)}
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#0B1F3A] gold-underline"
              data-testid="bio-chairman"
            >
              Read full biography <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* UNITED STATES MANAGEMENT TEAM */}
      <TeamSection
        eyebrow="United States"
        title="Management Team"
        subtitle="Institutional governance, capital strategy, compliance, legal oversight, and global growth — anchored in the United States."
        members={US_TEAM}
        onSelect={setSelected}
        testPrefix="us-team"
      />

      {/* CHINA MANAGEMENT TEAM */}
      <section className="bg-[#0B1F3A] text-[#F8F7F2] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="eyebrow" style={{ color: "#D4AF37" }}>China</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Management Team</h2>
            </div>
            <div className="md:col-span-7 text-[#F8F7F2]/80 leading-relaxed">
              <p>
                Our China-based leadership team provides direct execution capability across one of
                the world&apos;s largest energy and industrial markets. Led by senior executives from
                organizations including China Gas, Beijing Gas Group, ENN Group, major state-owned refineries,
                state-owned enterprises, and leading petrochemical organizations.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F8F7F2]/10">
            {CHINA_TEAM.map((p, i) => (
              <motion.button
                key={p.id}
                data-testid={`china-team-${p.id}`}
                onClick={() => setSelected(p)}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.55 }}
                className="text-left bg-[#0B1F3A] hover:bg-[#0F2646] transition p-8 group flex flex-col min-h-[240px]"
              >
                <PersonMonogram name={p.name} variant="light" />
                <div className="mt-5 font-display text-2xl leading-tight text-[#F8F7F2]">{p.name}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-[#D4AF37]">{p.role}</div>
                <p className="mt-4 text-sm text-[#F8F7F2]/70 leading-relaxed line-clamp-3">{p.short}</p>
                <div className="mt-auto pt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] opacity-0 group-hover:opacity-100 transition">
                  Read biography <ArrowUpRight size={12} />
                </div>
              </motion.button>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="eyebrow" style={{ color: "#D4AF37" }}>On-The-Ground Mandate</div>
              <div className="hairline mt-6 opacity-30" />
            </div>
            <div className="md:col-span-8">
              <p className="text-[#F8F7F2]/80 leading-relaxed mb-6">
                The China-based leadership team oversees on-the-ground operations, refinery
                relationships, port logistics, and compliance with domestic regulatory frameworks.
                Their responsibilities include:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CHINA_TEAM_RESPONSIBILITIES.map(r => (
                  <li key={r} className="flex gap-3 text-sm text-[#F8F7F2]/85">
                    <span className="text-[#D4AF37]">—</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ADVISORY BOARD */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="eyebrow" style={{ color: "#D4AF37" }}>Strategic Advisory Board</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Governance, expertise, and global relationships.</h2>
          </div>
          <div className="md:col-span-7 flex items-end">
            <p className="text-body">
              First Providence Capital is supported by an Advisory Board whose experience spans
              government, energy infrastructure, engineering,
              professional sports, and public leadership. The firm continues to expand its Advisory
              Board with globally recognized leaders from finance, infrastructure, international
              business, and institutional investment.
            </p>
          </div>
        </div>

        <div className="divide-y divide-[#0B1F3A]/10 border-t border-[#0B1F3A]/10">
          {ADVISORY_BOARD.map((m, i) => (
            <motion.button
              key={m.id}
              data-testid={`advisor-${m.id}`}
              onClick={() => setSelected(m)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.5 }}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-6 py-8 group hover:bg-[#F8F7F2] px-2 md:px-4 -mx-2 md:-mx-4 transition"
            >
              <div className="md:col-span-1">
                <PersonMonogram name={m.name} size="sm" />
              </div>
              <div className="md:col-span-3">
                <div className="font-display text-2xl text-[#0B1F3A] leading-tight">{m.name}</div>
              </div>
              <div className="md:col-span-4">
                <div className="text-xs uppercase tracking-widest text-[#3399FF]">{m.role}</div>
              </div>
              <div className="md:col-span-4 flex items-start justify-between gap-3">
                <div className="text-sm text-[#5F6B7A] leading-relaxed">{m.short}</div>
                <ArrowUpRight size={16} className="mt-1 text-[#D4AF37] shrink-0 opacity-60 group-hover:opacity-100 transition" />
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* INSTITUTIONAL RISK MANAGEMENT */}
      <section className="bg-[#F8F7F2] border-t border-[#0B1F3A]/10 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="eyebrow">Institutional Risk Management</div>
            <h2 className="font-display text-4xl mt-4 leading-tight">Every transaction, disciplined at every layer.</h2>
          </div>
          <div className="md:col-span-7 text-body">
            <p>
              Every transaction is evaluated through a disciplined framework integrating financial,
              operational, geopolitical, regulatory, environmental, and supply-chain risk. Our
              leadership includes former executives from Lloyd&apos;s of London, senior law enforcement
              professionals, former Assistant Attorneys General, compliance specialists, institutional
              finance executives, and commodity risk professionals who help ensure rigorous governance
              throughout the investment lifecycle.
            </p>
          </div>
        </div>
      </section>

      {/* BIO DIALOG */}
      <BioDialog person={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function TeamSection({ eyebrow, title, subtitle, members, onSelect, testPrefix }) {
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
      <div className="grid md:grid-cols-12 gap-10 mb-14">
        <div className="md:col-span-5">
          <div className="eyebrow">{eyebrow}</div>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">{title}</h2>
        </div>
        <div className="md:col-span-7 flex items-end">
          <p className="text-body">{subtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map((p, i) => (
          <motion.button
            key={p.id}
            data-testid={`${testPrefix}-${p.id}`}
            onClick={() => onSelect(p)}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05, duration: 0.55 }}
            className="text-left bg-white border border-[#0B1F3A]/10 p-8 hover:shadow-xl hover:-translate-y-1 transition group flex flex-col min-h-[280px]"
          >
            <PersonMonogram name={p.name} />
            <div className="mt-5 font-display text-2xl leading-tight text-[#0B1F3A]">{p.name}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-[#3399FF]">{p.role}</div>
            <p className="mt-4 text-sm text-[#5F6B7A] leading-relaxed line-clamp-4">{p.short}</p>
            <div className="mt-auto pt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              Read biography <ArrowUpRight size={12} />
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}

function initialsFor(name) {
  const parts = name.replace(/["']/g, "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function PersonMonogram({ name, variant = "dark", size = "md" }) {
  const initials = initialsFor(name);
  const sz = size === "xl" ? "h-20 w-20 text-2xl" : size === "sm" ? "h-10 w-10 text-xs" : "h-14 w-14 text-lg";
  const isDark = variant === "dark";
  return (
    <div className={`${sz} border ${isDark ? "border-[#D4AF37] text-[#0B1F3A] bg-[#F8F7F2]" : "border-[#D4AF37] text-[#D4AF37] bg-transparent"} font-display flex items-center justify-center tracking-tight`}>
      {initials}
    </div>
  );
}

function BioDialog({ person, onClose }) {
  return (
    <Dialog open={!!person} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl bg-[#F8F7F2] border-t-4 border-t-[#D4AF37] max-h-[85vh] overflow-y-auto thin-scroll">
        {person && (
          <>
            <DialogHeader>
              <div className="flex items-start gap-6">
                <PersonMonogram name={person.name} size="xl" />
                <div className="flex-1">
                  <DialogTitle asChild>
                    <h3 className="font-display text-3xl md:text-4xl text-[#0B1F3A] leading-tight">{person.name}</h3>
                  </DialogTitle>
                  <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[#3399FF]">{person.role}</div>
                </div>
              </div>
            </DialogHeader>
            <div className="hairline my-6" />
            <div className="space-y-5 text-body">
              {person.bio.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
